// src/pages/Contact.js
import React, { useState } from "react";
import { submitEnquiry } from "../firebase/services";
import { BRAND } from "../utils/constants";
import { isValidEmail } from "../utils/helpers";
import { toast } from "../components/Toast";
import "./Contact.css";

const CONTACT_INFO = [
  { icon: "📧", label: "General Inquiries",   email: BRAND.email.general },
  { icon: "🛒", label: "Sales & Orders",       email: BRAND.email.sales },
  { icon: "⚙️", label: "Technical Support",    email: BRAND.email.technical },
  { icon: "📣", label: "Marketing",            email: BRAND.email.marketing },
  { icon: "🏭", label: "Operations",           email: BRAND.email.operations },
  { icon: "💰", label: "Finance",              email: BRAND.email.finance },
];

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm]     = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim())          errs.name    = "Name is required";
    if (!form.email.trim())         errs.email   = "Email is required";
    else if (!isValidEmail(form.email)) errs.email = "Please enter a valid email";
    if (!form.message.trim())       errs.message = "Message is required";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus("sending");
    setErrors({});
    try {
      await submitEnquiry({ ...form, source: "contact-page" });
      setStatus("success");
      toast("Message sent! We'll get back to you soon.", "success");
      setForm(EMPTY);
    } catch (err) {
      console.error(err);
      setStatus("error");
      toast("Something went wrong. Please email us directly.", "error");
    }
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero__content">
            <span className="badge">📬 Reach Us</span>
            <h1 className="contact-hero__title">Get in Touch</h1>
            <p className="contact-hero__subtitle">
              We'd love to hear from you — product inquiries, partnerships, or support.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-grid">

            {/* ── Info column ───────────────────────────── */}
            <div className="contact-info">
              <h2 className="contact-info__heading">Contact Channels</h2>
              <p className="contact-info__sub">
                We have dedicated teams for each department. Pick the right channel
                for a faster response.
              </p>

              <div className="contact-channels">
                {CONTACT_INFO.map((c) => (
                  <a key={c.email} href={`mailto:${c.email}`} className="contact-channel">
                    <span className="contact-channel__icon">{c.icon}</span>
                    <div>
                      <span className="contact-channel__label">{c.label}</span>
                      <span className="contact-channel__email">{c.email}</span>
                    </div>
                  </a>
                ))}
              </div>

              <div className="contact-whatsapp">
                <span>💬</span>
                <div>
                  <strong>Quick Response via WhatsApp</strong>
                  <p>For faster enquiries, reach us instantly on WhatsApp.</p>
                  <a
                    href={`https://wa.me/${BRAND.whatsappNumber}`}
                    className="btn btn-gold"
                    target="_blank"
                    rel="noreferrer"
                    style={{ marginTop: 12, display: "inline-flex" }}
                  >
                    Open WhatsApp
                  </a>
                </div>
              </div>
            </div>

            {/* ── Form column ───────────────────────────── */}
            <div className="contact-form-wrap">
              <h2 className="contact-form__heading">Send a Message</h2>
              <p className="contact-form__sub">
                Fill in the form below and we'll respond within 24 hours.
              </p>

              {status === "success" ? (
                <div className="contact-success">
                  <span>✅</span>
                  <h3>Message Received!</h3>
                  <p>
                    Thank you for reaching out. Our team will contact you within 24 hours.
                  </p>
                  <button className="btn btn-outline" onClick={() => setStatus("idle")}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  <div className="form-row">
                    <div className={`form-group ${errors.name ? "form-group--error" : ""}`}>
                      <label htmlFor="name">Full Name *</label>
                      <input id="name" name="name" type="text" placeholder="Your name"
                        value={form.name} onChange={handleChange} />
                      {errors.name && <span className="form-error-msg">{errors.name}</span>}
                    </div>
                    <div className={`form-group ${errors.email ? "form-group--error" : ""}`}>
                      <label htmlFor="email">Email Address *</label>
                      <input id="email" name="email" type="email" placeholder="you@example.com"
                        value={form.email} onChange={handleChange} />
                      {errors.email && <span className="form-error-msg">{errors.email}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number</label>
                      <input id="phone" name="phone" type="tel" placeholder="+91 XXXXX XXXXX"
                        value={form.phone} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="subject">Subject</label>
                      <input id="subject" name="subject" type="text"
                        placeholder="Product enquiry, Partnership..."
                        value={form.subject} onChange={handleChange} />
                    </div>
                  </div>

                  <div className={`form-group ${errors.message ? "form-group--error" : ""}`}>
                    <label htmlFor="message">Message *</label>
                    <textarea id="message" name="message" rows={5}
                      placeholder="Tell us how we can help you..."
                      value={form.message} onChange={handleChange} />
                    {errors.message && <span className="form-error-msg">{errors.message}</span>}
                  </div>

                  <button type="submit" className="btn btn-primary"
                    disabled={status === "sending"} style={{ width: "100%" }}>
                    {status === "sending" ? "Sending..." : "Send Message →"}
                  </button>

                  <p className="form-note">
                    * Required fields. Submissions are sent to {BRAND.email.general}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
